'use client'
import { useState, useCallback } from 'react'
import { AxiosError } from 'axios'
import { signUpWithEmail as signUpRequest, activateEmail as activateRequest } from '@/root/shared-api/auth/authService'
import type { AuthError, SignUpEmailParams } from '@/root/shared-types/api/auth'
import { AUTH_ERROR_CODES, AUTH_ERROR_MESSAGES } from '@/root/shared-types/api/auth'

type SignUpResult = {
  success: boolean
  error?: AuthError
}

export const useSignUp = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)

  const signUpWithEmail = async (params: SignUpEmailParams): Promise<SignUpResult> => {
    setIsLoading(true)
    setError(null)

    try {
      await signUpRequest(params)
      return { success: true }
    } catch (err) {
      const errorData = err instanceof AxiosError
        ? (err as AxiosError<AuthError>).response?.data
        : undefined

      const authError: AuthError = {
        message: AUTH_ERROR_MESSAGES.SIGNUP_ERROR,
        code: errorData?.code ?? AUTH_ERROR_CODES.UNKNOWN,
      }

      setError(authError)
      return { success: false, error: authError }
    } finally {
      setIsLoading(false)
    }
  }

  const activateEmail = async (hash: string): Promise<SignUpResult & { token?: string }> => {
    setIsLoading(true)
    setError(null)

    try {
      const { data } = await activateRequest(hash)

      if (data?.token) {
        return { success: true, token: data.token }
      }

      const authError: AuthError = {
        message: AUTH_ERROR_MESSAGES.ACTIVATION_ERROR,
        code: AUTH_ERROR_CODES.ACTIVATION_INVALID,
      }
      setError(authError)
      return { success: false, error: authError }
    } catch (err) {
      const errorData = err instanceof AxiosError
        ? (err.response?.data as AuthError | undefined)
        : undefined

      const authError: AuthError = {
        message: AUTH_ERROR_MESSAGES.ACTIVATION_ERROR,
        code: errorData?.code ?? AUTH_ERROR_CODES.ACTIVATION_INVALID,
      }

      setError(authError)
      return { success: false, error: authError }
    } finally {
      setIsLoading(false)
    }
  }

  const reset = useCallback(() => {
    setIsLoading(false)
    setError(null)
  }, [])

  return { signUpWithEmail, activateEmail, isLoading, error, reset }
}
